import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart } from "@highcharts/react";
import { AMCReportTable } from "./AMCReportTable";
import { metricColors } from "../../../components/custom/analytics/palettes";
import { token } from "../../../../styled-system/tokens";

interface FrequencyDataItem {
  frequency_bucket: string;
  users_in_bucket?: number;
  impressions_in_bucket?: number;
  percent_of_total_users: number;
}

interface Props {
  frequencyData: FrequencyDataItem[];
}

const FrequencyLineChart = ({ frequencyData }: Props) => {
  const { t } = useTranslation(["amc"]);
  const chartOptions = useMemo((): Highcharts.Options => {
    const validData = frequencyData
      .filter((d) => d.frequency_bucket && d.frequency_bucket !== "TOTAL" && d.frequency_bucket !== "x0")
      .sort((a, b) => {
        const aNum = parseInt(a.frequency_bucket.replace("x", "").replace("+", ""));
        const bNum = parseInt(b.frequency_bucket.replace("x", "").replace("+", ""));
        return aNum - bNum;
      });

    const categories = validData.map((row) => row.frequency_bucket);
    const data = validData.map((row) => row.percent_of_total_users * 100);

    return {
      chart: {
        type: "column",
        height: 400,
        style: {
          fontSize: token("fontSizes.chart"),
          fontFamily: "Poppins",
          fontWieght: 300,
        },
      },
      title: {
        text: "",
      },
      xAxis: {
        categories,
        title: {
          text: t(($) => $.amc["Impressions per User"]),
        },
        labels: {},
      },
      yAxis: {
        title: {
          text: t(($) => $.amc["Percent of Total Users (%)"]),
        },
        labels: {
          format: "{value}%",
        },
        min: 0,
        max: 60,
      },
      tooltip: {
        pointFormat: "<b>{point.y:.2f}%</b>",
      },
      series: [
        {
          type: "column",
          name: t(($) => $.amc["Percent of Users"]),
          data,
          color: metricColors.units[0],
        },
      ],
      credits: {
        enabled: false,
      },
    };
  }, [frequencyData, t]);

  return <Chart options={chartOptions} />;
};

interface AMCReachImpressionFrequencyProps {
  reportData: FrequencyDataItem[];
  reportDetails: FrequencyDataItem[];
}

const AMCReachImpressionFrequency = ({ reportData, reportDetails }: AMCReachImpressionFrequencyProps) => {
  return (
    <>
      <FrequencyLineChart frequencyData={reportData} />

      <AMCReportTable reportData={reportData} reportDetails={reportDetails} />
    </>
  );
};

export default AMCReachImpressionFrequency;
